import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useDataCollection, useWizardMultistep, useWizardNavigation } from '../../../hooks';
import { ProgressBar } from '../../../component/ProgressBar';
import { sumRoomsArea } from '../../../tools/dataCollectionHelpers';
import { WizardStep } from '../../../types';

const isAsyncFunction = <T extends unknown>(val: unknown): val is Promise<T> =>
    typeof val === 'object' && val !== null && 'then' in val;

const areaDiffString = (areaDiff: number) => {
    const absAreaDiff = Math.round((Math.abs(areaDiff) + Number.EPSILON) * 100) / 100;
    const count =
        absAreaDiff === 0
            ? 'zero'
            : absAreaDiff === 1
            ? 'one'
            : absAreaDiff === 2 || absAreaDiff === 3 || absAreaDiff === 4
            ? 'few'
            : 'other';
    if (areaDiff > 0) {
        return {
            zero: `Přebývá ${absAreaDiff}`,
            one: `Přebývá ${absAreaDiff}`,
            few: `Přebývají ${absAreaDiff}`,
            other: `Přebývá ${absAreaDiff}`,
        }[count];
    } else {
        return {
            zero: `Chybí ${absAreaDiff}`,
            one: `Chybí ${absAreaDiff}`,
            few: `Chybí ${absAreaDiff}`,
            other: `Chybí ${absAreaDiff}`,
        }[count];
    }
};

function BottomBar(): ReactElement<'div'> | null {
    const { currentStep, currentStepIndex, currentProgress, goToPreviousStep, jumpToStep, setStepSkipped } =
        useWizardMultistep();
    const { getNavigateNextHandler, getNavigateBackHandler, nextButtonLabel, callFinal, setCallFinal } =
        useWizardNavigation();
    const { values } = useDataCollection();
    const [nextButtonLoading, setNextButtonLoading] = useState(false);

    const prevAvailable = currentStepIndex > 0;
    const nextAvailable = currentStep && currentStep.completed && currentStep.nextStepId;
    const isSkippable = currentStep && currentStep.skippable;
    const isFinal = currentStep && !currentStep.nextStepId;

    const roomAreaDiff = useMemo(() => {
        const countedArea = sumRoomsArea(values.roomsDistribution.rooms);
        return countedArea - values.areaTotal;
    }, [values.roomsDistribution.rooms, values.areaTotal]);

    const handlePrevPress = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            if (!currentStep) {
                return;
            }
            const navigationHandler = getNavigateBackHandler(currentStep.id);
            navigationHandler();
        },
        [goToPreviousStep],
    );

    const handleSkipPress = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            if (!currentStep || !currentStep.nextStepId) {
                return;
            }
            setStepSkipped(currentStep.id, true);
            jumpToStep(currentStep.nextStepId);
        },
        [currentStep],
    );

    const handleNextPress = useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            if (currentStep && currentStep.completed && currentStep.nextStepId) {
                const navigationHandler = getNavigateNextHandler(currentStep.id);
                const navigationResult = navigationHandler(currentStep.nextStepId);
                if (isAsyncFunction(navigationResult)) {
                    /*
                    some wizard step is preventing to navigate immediately and is doing some work.
                    Wait till it resolves, meanwhile disable the next button
                    */
                    setNextButtonLoading(true);
                    try {
                        await navigationResult;
                    } finally {
                        setNextButtonLoading(false);
                    }
                }
            }
        },
        [currentStep],
    );

    const nextButtonDisabled = nextButtonLoading || !nextAvailable;

    const wrapper = document.getElementById('wiz-progress-bar-wrapper');
    const bar = document.getElementById('wiz-progress-bar');

    useEffect(() => {
        if (wrapper && bar) {
            ProgressBar.init(wrapper, bar);
        }
    }, [currentStep]);

    const displayAreaDiff = currentStep?.id === WizardStep.RoomFloorAreas && roomAreaDiff;

    return (
        <div id={'wiz-progress-bar-wrapper'} className="wiz-progress-bar-wrapper">
            <div className="container">
                <div className="row">
                    <div className="col">
                        <div id={'wiz-progress-bar'} className="wiz-progress-bar" style={{ opacity: 0 }}>
                            <div className="row flex-ai-c">
                                <div className="col--6 col--md-6">
                                    <div className="wiz-progress-bar__item m-auto m-l-0">
                                        <div className="wiz-progress-bar__pointer">
                                            <div
                                                className="wiz-progress-bar__filled"
                                                style={{ width: `${currentProgress}%` }}
                                            />
                                        </div>
                                        <div className="wiz-progress-bar__title">{currentProgress}% hotovo</div>
                                    </div>
                                </div>
                                <div className="col--6 col--md-6">
                                    <div className="ta-r wiz-progress-bar__buttons">
                                        <a
                                            href="#"
                                            className="btn-bordered btn-bordered--gray-silver-chalice progress-bar__back"
                                            onClick={handlePrevPress}
                                            style={{
                                                opacity: prevAvailable ? 1 : 0.5,
                                                pointerEvents: prevAvailable ? 'auto' : 'none',
                                                cursor: prevAvailable ? 'pointer' : 'default',
                                            }}
                                        >
                                            <svg width="17.339" height="13.255" viewBox="0 0 17.339 13.255">
                                                <path id="Path_658" data-name="Path 658" d="M2.883,19.392l5.348-5.348a1.278,1.278,0,0,1,1.807,1.807L6.88,19.027H18.562a1.278,1.278,0,0,1,0,2.555H6.88l3.158,3.158a1.284,1.284,0,1,1-1.825,1.807l-5.33-5.33a1.3,1.3,0,0,1-.383-.913A1.227,1.227,0,0,1,2.883,19.392Z" transform="translate(-2.5 -13.675)" fill="currentColor"/>
                                            </svg>
                                        </a>
                                        {isSkippable && (
                                            <a href="#" className="m-r-10 md-m-r-20" onClick={handleSkipPress}>
                                                Přeskočit
                                            </a>
                                        )}
                                        {!isFinal && (
                                            <a
                                                href="#"
                                                className="btn otz-btn-progress-bar p-l-0 p-r-0 otz-opacity-50 pointer-events-none"
                                                onClick={handleNextPress}
                                                style={{
                                                    opacity: nextButtonDisabled ? 0.5 : 1,
                                                    pointerEvents: nextButtonDisabled ? 'none' : 'auto',
                                                    cursor: nextButtonDisabled ? 'default' : 'pointer',
                                                }}
                                            >
                                                {nextButtonLabel}
                                            </a>
                                        )}
                                        {isFinal && (
                                            <a
                                                href="#"
                                                className="btn otz-btn-progress-bar p-l-0 p-r-0 otz-opacity-50 pointer-events-none"
                                                onClick={() => {
                                                    setCallFinal(true);
                                                }}
                                                style={{
                                                    opacity: callFinal ? 0.5 : 1,
                                                    pointerEvents: callFinal ? 'none' : 'auto',
                                                    cursor: callFinal ? 'default' : 'pointer',
                                                }}
                                            >
                                                Dokončit
                                            </a>
                                        )}
                                    </div>
                                </div>
                                {displayAreaDiff && (
                                    <div className="col">
                                        <div className="wiz-progress-bar__infobar p-10 m-t-10 lg-flex flex-ai-c flex-jc-sb lg-p-l-20">
                                            <div className="m-b-10 md-flex flex-ai-c lg-m-b-0">
                                                <div className="flex flex-ai-c m-b-5 md-m-b-0">
                                                    <img
                                                        className="m-r-15"
                                                        src="/img/icon/warning-red.svg"
                                                        alt="Změnit obsah"
                                                    />
                                                    <div className="text-content otz-text-content--gray-alto">
                                                        <p>Pozor!</p>
                                                    </div>
                                                </div>
                                                <hr className="divider otz-divider--vertical none md-block md-m-r-30 md-m-l-55" />
                                                <div className="text-content text-content--md text-content--white p-r-15">
                                                    <p className="fw-600">V rozpisu místností něco nesedí</p>
                                                </div>
                                            </div>
                                            <div>
                                                <a href="#" className="btn w-100p ta-c lg-p-l-65 lg-p-r-65">
                                                    {areaDiffString(roomAreaDiff)} m<sup>2</sup>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BottomBar;
